import * as React from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { Select, Tooltip, Button, Spin, Alert, Empty } from 'antd';
import {
  PlayCircleOutlined,
  RedoOutlined,
  RightOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';

import { PATHS } from 'router/config';
import { ZoomStatus } from 'types';
import {
  useZoomLessons,
  ZoomLessonRow,
  useZoomMutations,
  useConditionalPolling,
} from 'core/hooks';
import { DateTime } from 'luxon';
import { todayInWarsaw, toApiDate } from 'core/utils/zoomTime';
import { lessonStatus } from 'core/utils/zoomLesson';
import {
  ZoomStatusBadge,
  ZoomDateRangeFilter,
  ZoomRowActions,
  IZoomRowAction,
  InfiniteScrollContainer,
  LoadingData,
} from 'molecules';
import { Colors } from 'services/ColorService';
import { getFont } from 'services/FontService';

const REFETCH_DELAY = 3_000;
const POLL_INTERVAL = 10_000;
const ACTIVE_STATUSES: ZoomStatus[] = [
  ZoomStatus.DOWNLOADING,
  ZoomStatus.UPLOADED,
  ZoomStatus.AI_PROCESSING,
];
const RETRY_BLOCKED: ZoomStatus[] = [
  ZoomStatus.DOWNLOADING,
  ZoomStatus.UPLOADED,
  ZoomStatus.AI_PROCESSING,
  ZoomStatus.SKIPPED,
];
const UNLINK_BLOCKED: ZoomStatus[] = [
  ZoomStatus.DOWNLOADING,
  ZoomStatus.UPLOADED,
  ZoomStatus.AI_PROCESSING,
];

type Row = ZoomLessonRow;

export const LessonsList = memo(() => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const initial = useMemo(() => queryString.parse(location.search), [
    location.search,
  ]);

  const [status, setStatus] = useState<ZoomStatus | undefined>(
    typeof initial.status === 'string'
      ? (initial.status as ZoomStatus)
      : undefined,
  );
  const [range, setRange] = useState<[DateTime, DateTime]>(() => {
    if (typeof initial.date === 'string') {
      const day = DateTime.fromISO(initial.date);
      return [day, day];
    }
    return [todayInWarsaw().minus({ days: 6 }), todayInWarsaw()];
  });

  const filters = useMemo(
    () => ({
      status,
      dateFrom: toApiDate(range[0]),
      dateTo: toApiDate(range[1]),
    }),
    [status, range],
  );

  const {
    lessons,
    loading,
    error,
    hasNextPage,
    startPolling,
    stopPolling,
    operations,
  } = useZoomLessons(filters);
  const mutations = useZoomMutations();

  const hasActiveRows = useMemo(
    () => lessons.some((row) => ACTIVE_STATUSES.includes(lessonStatus(row))),
    [lessons],
  );

  useConditionalPolling({
    startPolling,
    stopPolling,
    pollInterval: POLL_INTERVAL,
    predicate: hasActiveRows,
  });

  const refetchSoon = useCallback(() => {
    window.setTimeout(operations.refetch, REFETCH_DELAY);
  }, [operations]);

  const openLesson = useCallback(
    (id: string) => history.push(`${PATHS.zoomLessons}/${id}`),
    [history],
  );

  const statusOptions = useMemo(
    () =>
      Object.values(ZoomStatus).map((value) => ({
        value,
        label: `${t(`pages.zoom.status.${statusKey(value)}`)}`,
      })),
    [t],
  );

  const renderItem = (lesson: Row) => {
    const meta = lesson.zoomRecordingMeta;
    const st = lessonStatus(lesson);
    const group = lesson.module?.group;
    const retries = meta?.retryCount ?? 0;
    const retryBlocked = !meta || RETRY_BLOCKED.includes(st);
    const reprocessAllowed =
      Boolean(meta?.transcriptS3Key) &&
      [
        ZoomStatus.AI_COMPLETED,
        ZoomStatus.PUBLISHED,
        ZoomStatus.FAILED,
      ].includes(st);
    const unlinkBlocked = !meta || UNLINK_BLOCKED.includes(st);
    const isAuto = meta?.matchedBy === 'AUTO';
    const hasIndicators =
      Boolean(meta?.ambiguous) || retries > 0 || Boolean(meta?.errorMessage);

    const overflow: IZoomRowAction[] = meta
      ? [
          {
            key: 'reprocess',
            label: t('pages.zoom.lessons.actions.reprocess'),
            disabled: !reprocessAllowed,
            onClick: async () => {
              await mutations.reprocessAi.run(meta.id);
              refetchSoon();
            },
          },
          {
            key: 'unlink',
            label: t('pages.zoom.lessons.actions.unlink'),
            disabled: unlinkBlocked,
            danger: true,
            onClick: async () => {
              await mutations.unlink.run(meta.id);
              refetchSoon();
            },
          },
        ]
      : [];

    return (
      <Card key={lesson.id} onClick={() => openLesson(lesson.id)}>
        <CardTop>
          <TitleBlock>
            <Title>{lesson.name}</Title>
            <Subtitle>
              {group?.name}
              {group?.course?.name ? ` · ${group.course.name}` : ''}
            </Subtitle>
          </TitleBlock>
          <StatusBlock>
            <ZoomStatusBadge status={st} />
            <Tooltip title={t('pages.zoom.lessons.actions.open')}>
              <Chevron>
                <RightOutlined />
              </Chevron>
            </Tooltip>
          </StatusBlock>
        </CardTop>

        <MetaRow>
          <MetaItem>
            <CalendarOutlined /> {lesson.date}
          </MetaItem>
          <MetaItem>
            <ClockCircleOutlined /> {lesson.startTime?.slice(0, 5)}–
            {lesson.endTime?.slice(0, 5)}
          </MetaItem>
          {meta ? (
            <MetaItem>
              <MatchTag isAuto={isAuto}>
                {isAuto
                  ? t('pages.zoom.lessons.matchedAuto')
                  : t('pages.zoom.lessons.matchedManual')}
              </MatchTag>
              {meta.matchedByUser ? (
                <span>{meta.matchedByUser.email}</span>
              ) : null}
            </MetaItem>
          ) : null}
        </MetaRow>

        {hasIndicators ? (
          <Indicators>
            {meta?.ambiguous ? (
              <Tooltip title={t('pages.zoom.lessons.indicators.ambiguous')}>
                <Indicator color={Colors.WarningDefault}>
                  <WarningOutlined />{' '}
                  {t('pages.zoom.lessons.columns.ambiguous')}
                </Indicator>
              </Tooltip>
            ) : null}
            {retries > 0 ? (
              <Indicator color={Colors.Gray}>
                <RedoOutlined />{' '}
                {t('pages.zoom.lessons.indicators.retries', { count: retries })}
              </Indicator>
            ) : null}
            {meta?.errorMessage ? (
              <Tooltip title={meta.errorMessage}>
                <Indicator color={Colors.ErrorDefault}>
                  <ExclamationCircleOutlined />{' '}
                  {t('pages.zoom.lessons.errorTitle')}
                </Indicator>
              </Tooltip>
            ) : null}
          </Indicators>
        ) : null}

        <Divider />

        <Actions onClick={(event: React.MouseEvent) => event.stopPropagation()}>
          {lesson.video ? (
            <Button
              icon={<PlayCircleOutlined />}
              onClick={() => window.open(lesson.video as string, '_blank')}
            >
              {t('pages.zoom.lessons.video')}
            </Button>
          ) : null}
          {meta ? (
            <Tooltip title={t('pages.zoom.lessons.actions.retryHint')}>
              <Button
                type="primary"
                icon={<RedoOutlined />}
                disabled={retryBlocked}
                onClick={async () => {
                  await mutations.retry.run(meta.id);
                  refetchSoon();
                }}
              >
                {st === ZoomStatus.MATCHED
                  ? t('pages.zoom.lessons.actions.startProcessing')
                  : t('pages.zoom.lessons.actions.retry')}
              </Button>
            </Tooltip>
          ) : null}
          <Spacer />
          {overflow.length ? <ZoomRowActions actions={overflow} /> : null}
        </Actions>
      </Card>
    );
  };

  return (
    <Page>
      <PageHeader>
        <HeaderTitleRow>
          <HeaderTitle>{t('pages.zoom.lessons.listTitle')}</HeaderTitle>
          <HeaderCount>
            {t('pages.zoom.lessons.count', { count: lessons.length })}
          </HeaderCount>
        </HeaderTitleRow>
        <HeaderSubtitle>{t('pages.zoom.lessons.listSubtitle')}</HeaderSubtitle>
      </PageHeader>

      <FilterCard>
        <FilterItem>
          <FilterLabel>{t('pages.zoom.lessons.filters.status')}</FilterLabel>
          <Select
            allowClear
            style={{ width: 220 }}
            placeholder={t('pages.zoom.lessons.filters.allStatuses')}
            value={status}
            options={statusOptions}
            onChange={(value) => setStatus(value as ZoomStatus | undefined)}
          />
        </FilterItem>
        <FilterItem>
          <FilterLabel>{t('pages.zoom.lessons.filters.period')}</FilterLabel>
          <ZoomDateRangeFilter
            value={range}
            onChange={(next) => {
              if (next) {
                setRange(next);
              }
            }}
          />
        </FilterItem>
      </FilterCard>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {loading && !lessons.length ? (
        <CenteredPanel>
          <Spin />
        </CenteredPanel>
      ) : null}

      {!loading && !lessons.length ? (
        <CenteredPanel>
          <Empty description={t('pages.zoom.lessons.noData')} />
        </CenteredPanel>
      ) : null}

      {lessons.length ? (
        <InfiniteScrollContainer
          loadMore={operations.fetchMore}
          hasMore={hasNextPage}
          dataLength={lessons.length}
          loading={loading}
        >
          <List>{lessons.map(renderItem)}</List>
        </InfiniteScrollContainer>
      ) : null}

      {Boolean(lessons.length) && loading ? <LoadingData /> : null}
    </Page>
  );
});

/** Maps a ZoomStatus enum value to its camelCase i18n key. */
const statusKey = (value: string): string => {
  const map: Record<string, string> = {
    MATCHED: 'matched',
    DOWNLOADING: 'downloading',
    UPLOADED: 'uploaded',
    AI_PROCESSING: 'aiProcessing',
    AI_COMPLETED: 'aiCompleted',
    PUBLISHED: 'published',
    FAILED: 'failed',
    SKIPPED: 'skipped',
    NO_RECORDING: 'noRecording',
  };
  return map[value] ?? value.toLowerCase();
};

const Page = styled.div`
  padding: 16px 0 40px;
`;

const PageHeader = styled.div`
  margin-bottom: 16px;
`;

const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled.h2`
  ${getFont({ fontSize: 22, lineHeight: 28, familyWeight: 'bold' })};
  color: ${Colors.Black};
  margin: 0;
`;

const HeaderCount = styled.span`
  background: ${Colors.BasicBlueLight};
  color: ${Colors.Blue};
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 13px;
`;

const HeaderSubtitle = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${Colors.Gray};
`;

const FilterCard = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: flex-end;
  background: ${Colors.White};
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.span`
  font-size: 12px;
  color: ${Colors.Gray};
`;

const CenteredPanel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: ${Colors.White};
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  background: ${Colors.White};
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  box-shadow: 0 1px 2px ${Colors.BlueShadow};
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${Colors.Blue};
    box-shadow: 0 6px 20px ${Colors.BlueShadowRow};
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const Title = styled.div`
  ${getFont({ fontSize: 16, lineHeight: 22, familyWeight: 'semibold' })};
  color: ${Colors.Blue};

  ${Card}:hover & {
    text-decoration: underline;
  }
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: ${Colors.Gray};
  margin-top: 2px;
`;

const StatusBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const Chevron = styled.span`
  color: ${Colors.Gray};
  font-size: 16px;
  display: inline-flex;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 12px;
  font-size: 13px;
  color: ${Colors.Black};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${Colors.Gray};

  & .anticon {
    color: ${Colors.Gray};
  }
`;

const MatchTag = styled.span<{ isAuto: boolean }>`
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ isAuto }) => (isAuto ? Colors.Blue : Colors.Black)};
  background: ${({ isAuto }) =>
    isAuto ? Colors.BasicBlueLight : Colors.LightGray};
`;

const Indicators = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 10px;
`;

const Indicator = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: ${({ color }) => color};
`;

const Divider = styled.div`
  height: 1px;
  background: ${Colors.LightGray};
  margin: auto 0 14px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Spacer = styled.div`
  flex: 1;
`;
